import {useQuery} from '@tanstack/react-query';
import React, {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {useDebouncedCallback} from 'use-debounce';
import {fetchRoomsIdentifications} from '../../../../service/room.service';
import Select, {Option} from '../../../components/common/Select';
import {IFetchRoom} from '../../../../incase-domain/rooms/room.editor';
import Label from '../../../../incase-domain/components/label/Label';

interface PresentationRoomsFilterProps {
    roomParams?: {roomId: string; identification?: string; group?: string};
    setRoomParams: Dispatch<SetStateAction<{roomId: string; identification?: string; group?: string} | undefined>>;
    rooms: IFetchRoom[] | undefined;
}

function PresentationRoomsFilter(props: PresentationRoomsFilterProps): JSX.Element | null {
    const {roomParams, setRoomParams, rooms} = props;
    const {t} = useTranslation();
    const location = useLocation();

    const [, setRoomInputSearch] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    const debouncedSetSearchTerm = useDebouncedCallback(setSearchTerm, 300);
    const debouncedSetRoomParamsParent = useDebouncedCallback(setRoomParams, 300);

    const initialOption = {label: 'Any', value: ''};
    const roomOptions: Option[] = [
        initialOption,
        ...(rooms?.map((room) => ({
            value: room._id,
            label: room.roomName,
        })) || []),
    ];

    const {data: roomIdentifications} = useQuery(
        ['roomIdentification', roomParams?.roomId, searchTerm],
        () => (roomParams?.roomId ? fetchRoomsIdentifications(roomParams.roomId, searchTerm) : []),
        {
            keepPreviousData: true,
        }
    );

    const selectedRoom = useMemo(() => {
        if (!roomParams?.roomId) return undefined;

        return rooms?.find((room): boolean => room._id === roomParams?.roomId);
    }, [roomParams?.roomId, rooms]);

    const identificationOptions = generateOptions(roomIdentifications);
    const groupIdItems = generateOptions(selectedRoom?.identification?.options);

    useEffect(() => {
        debouncedSetRoomParamsParent(roomParams);
    }, [roomParams, debouncedSetRoomParamsParent]);

    useEffect(() => {
        const rId = location.state && location.state.roomId;
        if (rId) setRoomParams({roomId: rId});
    }, [location.state, setRoomParams]);

    if (!rooms?.length) return null;

    return (
        <div className="c-reportFilter__group">
            <div className="c-reportFilter__group_filter">
                <Label>{t('reportPage.filterRoomsTitle')}</Label>
                <Select
                    options={roomOptions}
                    value={roomOptions.find((option) => option.value === roomParams?.roomId) || initialOption}
                    onChange={(selectedOption) => {
                        setRoomParams({
                            roomId: typeof selectedOption?.value === 'string' ? selectedOption.value : '',
                            group: '',
                            identification: '',
                        });
                    }}
                    onInputChange={(e) => setRoomInputSearch(e)}
                />
            </div>

            {roomParams?.roomId && (
                <>
                    {selectedRoom?.identification?.groupIdentificationOn && groupIdItems && (
                        <div className="c-reportFilter__group_filter">
                            <Label>{selectedRoom?.identification?.groupIdentificationLabel}</Label>
                            <Select
                                placeholder={t('presEditPage.editSidebar.elements.selectPlaceholder')}
                                value={
                                    roomParams?.group
                                        ? {
                                              label: roomParams.group,
                                              value: roomParams.group,
                                          }
                                        : undefined
                                }
                                onChange={(e) =>
                                    e &&
                                    setRoomParams((curr) =>
                                        curr
                                            ? {
                                                  ...curr,
                                                  group: e.value as string,
                                              }
                                            : undefined
                                    )
                                }
                                options={groupIdItems}
                            />
                        </div>
                    )}

                    {selectedRoom?.identification?.specificIdentificationOn && (
                        <div className="c-reportFilter__group_filter">
                            <Label>{selectedRoom?.identification?.specificIdentificationLabel}</Label>
                            <Select
                                placeholder={t('presEditPage.editSidebar.elements.selectPlaceholder')}
                                value={
                                    roomParams?.identification
                                        ? {label: roomParams.identification, value: roomParams.identification}
                                        : undefined
                                }
                                onInputChange={debouncedSetSearchTerm}
                                onChange={(e) =>
                                    e &&
                                    setRoomParams((curr) =>
                                        curr
                                            ? {
                                                  ...curr,
                                                  identification: e.value as string,
                                              }
                                            : undefined
                                    )
                                }
                                options={identificationOptions}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function generateOptions(data: string[] = []): {label: string; value: string}[] {
    const empty = [{label: 'Any', value: ''}];
    if (!data || !data.length) return empty;

    return empty.concat(data.map((option) => ({label: option, value: option})));
}

export default PresentationRoomsFilter;
